<template>
    <div
        class="relative overflow-hidden"
        @mousemove="handleEdgeHover"
        @mouseleave="stopEdgeScroll"
    >
        <!-- Left fade gradient -->
        <div
            v-if="showLeftGradient"
            class="pointer-events-none absolute left-0 top-0 z-10 h-full w-20 select-none bg-gradient-to-r from-white via-white/40"
        ></div>
        <!-- Right fade gradient -->
        <div
            v-if="showRightGradient"
            class="pointer-events-none absolute right-0 top-0 z-10 h-full w-20 select-none bg-gradient-to-l from-white via-white/40"
        ></div>
        <!-- Scrollable container -->
        <div
            ref="scrollContainer"
            class="scrollbar-hide"
            :class="[
                containerClass,
                isOverflowing ? 'overflow-x-auto' : 'overflow-x-hidden',
                isOverflowing ?
                    isDragging ? 'cursor-grabbing select-none'
                    :   'cursor-grab'
                :   '',
            ]"
            @scroll="handleScroll"
            @mousedown="handleMouseDown"
        >
            <slot></slot>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    containerClass?: string;
}

withDefaults(defineProps<Props>(), {
    containerClass: '',
});

// Scroll gradient states
const showLeftGradient = ref(false);
const showRightGradient = ref(false);
const scrollContainer = ref<HTMLElement>();
const isOverflowing = ref(false);

// Drag scrolling states
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);

// Edge-hover auto-scroll (desktop only)
const EDGE_ZONE_WIDTH = 50;
const MAX_SCROLL_SPEED = 6;
const hasHoverCapability = ref(typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches);
const edgeScrollRafId = ref<number | null>(null);
const edgeScrollSpeed = ref(0);

const updateOverflowState = (container: HTMLElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const epsilon = 2;
    const overflowAmount = scrollWidth - clientWidth;
    const hasOverflow = overflowAmount > epsilon;

    isOverflowing.value = hasOverflow;
    showLeftGradient.value = hasOverflow && scrollLeft > epsilon;
    showRightGradient.value = hasOverflow && overflowAmount - scrollLeft > epsilon;

    if (!hasOverflow) {
        isDragging.value = false;
    }
};

// Handle scroll to show/hide gradients
const handleScroll = (event: Event) => {
    const container = event.target as HTMLElement;
    if (!container) return;

    updateOverflowState(container);
};

// Handle mouse down for drag scrolling
const handleMouseDown = (event: MouseEvent) => {
    if (!scrollContainer.value || !isOverflowing.value) return;

    isDragging.value = true;
    startX.value = event.pageX - scrollContainer.value.offsetLeft;
    scrollLeft.value = scrollContainer.value.scrollLeft;

    // Prevent text selection during drag
    event.preventDefault();

    // Add global event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
};

// Handle mouse move for drag scrolling
const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging.value || !scrollContainer.value) return;

    event.preventDefault();

    const x = event.pageX - scrollContainer.value.offsetLeft;
    const walk = (x - startX.value) * 2; // Multiply by 2 for faster scrolling
    scrollContainer.value.scrollLeft = scrollLeft.value - walk;
};

// Handle mouse up to stop drag scrolling
const handleMouseUp = () => {
    isDragging.value = false;

    // Remove global event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
};

const edgeScrollLoop = () => {
    if (!scrollContainer.value || edgeScrollSpeed.value === 0) {
        edgeScrollRafId.value = null;
        return;
    }

    scrollContainer.value.scrollLeft += edgeScrollSpeed.value;
    updateOverflowState(scrollContainer.value);
    edgeScrollRafId.value = requestAnimationFrame(edgeScrollLoop);
};

const handleEdgeHover = (event: MouseEvent) => {
    if (!hasHoverCapability.value || !scrollContainer.value || !isOverflowing.value || isDragging.value) return;

    const rect = scrollContainer.value.getBoundingClientRect();
    const cursorX = event.clientX - rect.left;
    const containerWidth = rect.width;

    if (cursorX < EDGE_ZONE_WIDTH) {
        const proximity = 1 - cursorX / EDGE_ZONE_WIDTH;
        edgeScrollSpeed.value = -(proximity * MAX_SCROLL_SPEED);
    } else if (cursorX > containerWidth - EDGE_ZONE_WIDTH) {
        const proximity = 1 - (containerWidth - cursorX) / EDGE_ZONE_WIDTH;
        edgeScrollSpeed.value = proximity * MAX_SCROLL_SPEED;
    } else {
        edgeScrollSpeed.value = 0;
    }

    if (edgeScrollSpeed.value !== 0 && edgeScrollRafId.value === null) {
        edgeScrollRafId.value = requestAnimationFrame(edgeScrollLoop);
    }
};

const stopEdgeScroll = () => {
    edgeScrollSpeed.value = 0;
    if (edgeScrollRafId.value !== null) {
        cancelAnimationFrame(edgeScrollRafId.value);
        edgeScrollRafId.value = null;
    }
};

// Check initial scroll state
const checkScrollState = () => {
    nextTick(() => {
        if (scrollContainer.value) {
            updateOverflowState(scrollContainer.value);
        }
    });
};

// Observe the scroll container's dimensions to detect overflow changes
// from window resizes, sidebar toggles, layout shifts, etc.
useResizeObserver(scrollContainer, () => {
    if (scrollContainer.value) {
        updateOverflowState(scrollContainer.value);
    }
});

// Observe slot content changes to detect when children are added/removed
useMutationObserver(
    scrollContainer,
    () => {
        checkScrollState();
    },
    { childList: true, subtree: true },
);

onMounted(() => {
    checkScrollState();
});

// Cleanup event listeners on component unmount
onUnmounted(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    stopEdgeScroll();
});

// Expose methods for parent components
defineExpose({
    checkScrollState,
});
</script>

<style scoped>
.scrollbar-hide {
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
}
</style>
